import { useEffect, useMemo, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import { useMatch } from 'react-router-dom';

import {
  DrawerProvider,
  useDrawer,
  Inset,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { useAbility } from '@cast/shared-ability-data-access';
import { Notification, NotificationSeverity } from '@cast/types';
import { usePrevious } from '@cast/utils';

import { ToPortal } from 'core/portals';
import { useNotificationDrawer } from 'features/notifications/hooks/useNotificationDrawer';
import { useAckNotificationsMutation } from 'hooks/mutations/notifications';
import { useGetNotificationsQuery } from 'hooks/queries/notifications';

import { CriticalNotificationItem } from './CriticalNotificationItem';
import {
  GroupedNotification,
  isGroupedNotification,
  NotificationItem,
} from './types';
import { groupNotificationsByType } from './utils';

const filterByCluster = (
  items: Notification[],
  clusterId: string | undefined
): Notification[] => {
  if (!clusterId) return items;
  return items.filter(
    (item) => !item.clusterMetadata?.id || item.clusterMetadata.id === clusterId
  );
};

const CriticalNotificationListInner = () => {
  const { openNotificationDrawer } = useNotificationDrawer();
  const { mutate: ackNotifications } = useAckNotificationsMutation();
  const canAckNotifications = useAbility().can(
    'update',
    'OrganizationNotifications'
  );
  // TODO: once old UI support is removed remove clusterMatchOldUI
  const clusterMatchOldUI = useMatch('/external-clusters/:clusterId/*');
  const clusterMatchNewUI = useMatch(
    '/automation/external-clusters/:clusterId/*'
  );
  const currentClusterId =
    clusterMatchOldUI?.params?.clusterId ||
    clusterMatchNewUI?.params?.clusterId;

  const { drawers } = useDrawer();
  const [isExpanded, setExpanded] = useState(false);
  const [expansionTransitionEnded, setExpansionTransitionEnded] =
    useState(false);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const isNotificationDrawerOpen = useMemo(() => {
    return !!Object.values(drawers).find((drawer) => {
      return !!(drawer.props as { notification?: Notification })?.notification;
    });
  }, [drawers]);

  const {
    notificationsResponse: { items },
  } = useGetNotificationsQuery({
    filter: {
      onlyUnresolved: true,
      isExpired: false,
      severities: [NotificationSeverity.CRITICAL],
    },
  });
  const prevItems = usePrevious(items);
  const prevClusterId = usePrevious(currentClusterId);
  const [visibleNotifications, setVisibleNotifications] = useState<
    GroupedNotification[]
  >([]);
  const notificationTotal = visibleNotifications.length;

  useEffect(() => {
    if (!isEqual(items, prevItems) || currentClusterId !== prevClusterId) {
      setVisibleNotifications(
        groupNotificationsByType(filterByCluster(items, currentClusterId))
      );
    }
  }, [items, prevItems, currentClusterId, prevClusterId]);

  useEffect(() => {
    const controller = new AbortController();

    if (
      elementRef.current &&
      isExpanded &&
      expansionTransitionEnded &&
      !isNotificationDrawerOpen
    ) {
      const expandedHeight = elementRef.current.scrollHeight;
      const { top, left, right } = elementRef.current.getBoundingClientRect();

      window.addEventListener(
        'mousemove',
        throttle((e: MouseEvent) => {
          const isOutsideHorizontalBounds =
            e.clientX < left || e.clientX > right;
          const isOutsideVerticalBounds =
            e.clientY < top || e.clientY > top + expandedHeight;

          if (isOutsideHorizontalBounds || isOutsideVerticalBounds) {
            setExpanded(false);
            setExpansionTransitionEnded(false);
            controller.abort();
          }
        }, 350),
        {
          signal: controller.signal,
        }
      );
    }

    return () => {
      controller.abort();
    };
  }, [isExpanded, expansionTransitionEnded, isNotificationDrawerOpen]);

  const handleNotificationDismiss = (notification: GroupedNotification) => {
    // Viewers can hide the banner locally but lack permission to ack on the
    // server — skip the request to avoid an error toast.
    if (canAckNotifications) {
      ackNotifications(
        notification.items.filter(Boolean).map((item) => item.id) as string[]
      );
    }

    setVisibleNotifications(
      visibleNotifications.filter((item) => item.id !== notification.id)
    );
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    openNotificationDrawer({
      // Grouped notification can only be clicked directly if it has a single item
      notification: isGroupedNotification(notification)
        ? notification.items[0]
        : notification,
    });
  };

  if (!notificationTotal) {
    return null;
  }

  const multipleNotifications = notificationTotal > 1;

  return (
    <ToPortal portal="PAGE_HEADER">
      <Inset>
        <Stack
          mt={multipleNotifications ? 10 : 0}
          height={95}
          gap={4}
          sx={{
            isolation: 'isolate',
            zIndex: 100,
            position: 'relative',
            top: 20,
            overflow: 'visible',
          }}
          onTransitionEnd={() => {
            if (isExpanded) {
              setExpansionTransitionEnded(true);
            }
          }}
          ref={elementRef}
          data-testid="critical-notification-list"
        >
          {multipleNotifications && (
            <Typography
              variant="P12B"
              color={tokens.theme.fg.structural.secondary}
              sx={{
                position: 'absolute',
                top: -20,
              }}
            >
              {notificationTotal} Critical alerts
            </Typography>
          )}
          {visibleNotifications.map((notification, index) => (
            <CriticalNotificationItem
              key={notification.id}
              notification={notification}
              index={index}
              notificationTotal={notificationTotal}
              isExpanded={isExpanded}
              setExpanded={setExpanded}
              onClick={handleNotificationClick}
              onDismiss={handleNotificationDismiss}
            />
          ))}
        </Stack>
      </Inset>
    </ToPortal>
  );
};

export const CriticalNotificationList = () => {
  return (
    <DrawerProvider>
      <CriticalNotificationListInner />
    </DrawerProvider>
  );
};
